"use client"

import {useEffect, useMemo, useState} from "react"
import * as translator from "../../utils/translator";
import { clientConfig } from "../../config";

import CartItemV25 from "../../pages/Shipping/Components/OtcCartPopUp/CartItemV25"
import { useCartContext } from "../../customHooks/useCart";
import * as http from "../../utils/http";
import { groupProductBlocksByRiskGroup, groupProductsByRiskGroup } from "../../utils/groupProductsByRiskGroup";
import ShippingProgressBarV25 from "./ShippingProgressBarV25";


interface CartSliderProps {
    isOpen: boolean;
    onClose: () => void;
    country: string
  }



const CartSliderV25:React.FC<CartSliderProps> = ({country, isOpen,onClose }) => {
  const {getCart,saveCart,removeProductItemsFromCart,incrementProductItemQuantity,decrementProductItemQuantity,setRiskGroupConfirmation, cartCurrency} = useCartContext()
  let language  = translator.getLanguageByCountry(country)
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [loadingRiskGroupID, setLoadingRiskGroupID] = useState<number | null>(null);
  const cart = getCart()
  const cartProducts = cart.products
  const hasRiskBlock = Boolean(cart.risk_groups?.some((group) => group.status !== 'clear'))
  // ?? null keeps a real threshold of 0 ("always free") distinguishable from "not yet loaded" (null)
  const freeShippingThreshold = cart.otcShippingDetails?.otc_shipping_threshold ?? null
  // custom_shipping_fee already holds the computed OTC fee (fee value or 0 when free)
  const shippingFeeOTC = cart.custom_shipping_fee || 0

  const setSubmitButtonDisabled = (disabled: boolean) => {
    setIsSubmitButtonDisabled(disabled);
  };

  const handleRemove = async (productToRemove: ProductItem) => {
   setSubmitButtonDisabled(true);
   await removeProductItemsFromCart([productToRemove.id])
   setSubmitButtonDisabled(false);
  };

  const handleIncrement = async (productItem: ProductItem) => {
    setSubmitButtonDisabled(true);
    await incrementProductItemQuantity(productItem, false);
    setSubmitButtonDisabled(false);
  }

  const handleDecrement = async (productItem: ProductItem) => {
    setSubmitButtonDisabled(true);
    await decrementProductItemQuantity(productItem, false);
    setSubmitButtonDisabled(false);
  }

  const handleRiskGroupConfirmationChange = async (riskGroupID: number, confirmed: boolean) => {
    if (loadingRiskGroupID !== null) return;
    setLoadingRiskGroupID(riskGroupID);
    try {
      const result = await setRiskGroupConfirmation(riskGroupID, confirmed);
      if (result.status !== 'ok') setLoadingRiskGroupID(null);
    } catch {
      setLoadingRiskGroupID(null);
    }
  };


// Helper function to convert CartProductView to ProductItem
const convertToProductItem = (cartProduct: CartProductView): ProductItem => {
  const isOnlyPrescription = cart.only_prescription && cartProduct.type === "prescription_drug"
  return {
    id: cartProduct.product_item_id,
    price: isOnlyPrescription ? 0 : cartProduct.item_price,
    price_before_discount: cartProduct.price_before_discount || null,
    official_price: cartProduct.official_price,
    type: cartProduct.type,
    custom_quantity_title: cartProduct.quantityStr,
    imgSrc: isOnlyPrescription ? clientConfig.IMAGES_URL + clientConfig.PRESCRIPTION_IMAGE || '' : cartProduct.imageSrc || '',
    title: cartProduct.title,
    quantity: cartProduct.quantity.toString(),
    is_test_lab: cartProduct.is_test_lab,
    risk_group_id: cartProduct.risk_group_id,
    risk_group_confirmed: cartProduct.risk_group_confirmed,
  };
};

  useEffect(() => {
    if(cartProducts.length === 0){
      onClose()
    }
  }, [cartProducts])

  useEffect(() => {
    if (!isOpen) return;

    http.getFromServer('cart', [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response) => {
        if (!response.data?.cart || !Array.isArray(response.data?.products)) return;
        saveCart({ ...response.data.cart, products: response.data.products, timekitBooking: response.data.timekitBooking || null });
      })
      .catch((error) => window.clientLogger.error('Failed to refresh cart slider', error));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('otc-modal-open');
    } else {
      document.body.classList.remove('otc-modal-open');
    }
    return () => {
      document.body.classList.remove('otc-modal-open');
    };
  }, [isOpen]);

  const handleToCheckoutClick = () => {
    const token = window.eventsTracking.getData("token");
    if(!token || token === "undefined"){
      window.location.href = `/sign_up/${country}/${language}`;
    } else if (cart.should_id_verify) {
      window.location.href = `/customer-verification/${country}/${language}`;
    } else{
      window.location.href = `/shipping/${country}/${language}`;
    }
  }


  const convertedProducts = useMemo(() => {
    const sortedProducts = cartProducts.sort((a, b) => {
      if (a.type === "free_drug") return 1;
      if (b.type === "free_drug") return -1;
      return 0;
    });
    return groupProductsByRiskGroup(sortedProducts).map(convertToProductItem);
  }, [cartProducts]);

  const productBlocks = useMemo(() => groupProductBlocksByRiskGroup(convertedProducts), [convertedProducts]);

  const total = useMemo(() => {
    const treatmentFee = cart.only_prescription ? cart.treatment_fee || 0 : 0
    return convertedProducts.reduce((acc, product) => acc + product.price * parseInt(product.quantity), treatmentFee)
  }, [convertedProducts])

  const totalDiscount = useMemo(() => {
    return convertedProducts.reduce((sum, product) => {
      if (product.price_before_discount !== null && product.price_before_discount !== undefined) {
        const quantity = parseInt(product.quantity) || 1;
        const discount = (product.price_before_discount - product.price) * quantity;
        return sum + (discount > 0 ? discount : 0);
      }
      return sum;
    }, 0);
  }, [convertedProducts])

  const isOtcOnlyFlow = useMemo(() => {
    return convertedProducts.every(product => product.type === "free_drug")
  }, [convertedProducts])

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed top-0 right-0 w-full h-full z-[9999] transition-[background] duration-300 ${
          isOpen ? "bg-black/70 pointer-events-auto" : "bg-transparent pointer-events-none"
        }`}
        onClick={onClose}
      >
        {/* Cart panel */}
        <div
          id="cart-slider-panel"
          className={`fixed top-0 h-full w-[504px] max-[768px]:w-screen bg-transparent transition-[right] duration-300 ease-out overflow-y-auto z-[10000] flex flex-col max-h-screen ${
            isOpen && convertedProducts.length > 0 ? "right-0" : "-right-[504px]"
          }`}
          style={{ display: convertedProducts.length > 0 ? "flex" : "none" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top section - flex-1 to stretch full height */}
          <div className="bg-white flex flex-col gap-[28px] px-[24px] pt-[16px] pb-[24px] max-[768px]:px-[16px] flex-1 rounded-bl-[24px] rounded-br-[24px]">
            {/* Header row */}
            <div className="flex items-center justify-between h-[31px]">
              <div className="flex gap-[4px] items-center font-poppins text-[22px] leading-[29px]">
                <span className="font-medium text-[#1a1a2e]">
                  {translator.translate(language, "shipping", "otcCartSlider/title")}
                </span>
                <span className="font-normal text-[#999999]">
                  ({convertedProducts.length})
                </span>
              </div>
              <button
                className="text-[#999999] hover:text-[#1a1a2e] text-[24px] cursor-pointer border-0 bg-transparent leading-none w-[24px] h-[24px] flex items-center justify-center"
                onClick={onClose}
              >
                &times;
              </button>
            </div>

            <div className="flex flex-col gap-[16px] flex-1">
              {/* Cart items */}
              <div className="flex flex-col gap-[4px] flex-1 max-h-[50vh]">
                <div className="flex flex-col gap-0 overflow-y-auto overscroll-contain min-h-0 flex-1">
                  {productBlocks.map((block, blockIndex) => {
                    const riskGroup = block.riskGroupID ? cart.risk_groups?.find((group) => group.id === block.riskGroupID) : undefined;
                    const blocked = Boolean(riskGroup && riskGroup.status !== 'clear');
                    return (
                      <div key={`${block.riskGroupID || 'product'}-${blockIndex}`}>
                        <div className={blocked ? 'mt-[8px] flex flex-col rounded-[10px] border border-[#df5555] p-[16px]' : ''}>
                          {block.products.map((product, productIndex) => (
                            <div key={product.id} className={blocked ? `${productIndex > 0 ? 'pt-[8px]' : ''} ${productIndex < block.products.length - 1 ? 'pb-[8px] border-b border-[#e5e7eb]' : ''}` : ''}>
                              <CartItemV25
                                handleIncrement={handleIncrement}
                                isIncrementDisabled={blocked}
                                handleDecrement={handleDecrement}
                                onRemove={() => handleRemove(product)}
                                product={product}
                                riskGroup={riskGroup}
                                insideRiskGroup={blocked}
                                showRiskGroupWarning={blocked && productIndex === block.products.length - 1}
                                riskGroupConfirmationDisabled={block.riskGroupID !== undefined && loadingRiskGroupID === block.riskGroupID}
                                onRiskGroupConfirmationChange={(confirmed) => block.riskGroupID && handleRiskGroupConfirmationChange(block.riskGroupID, confirmed)}
                                language={language}
                                country={country}
                              />
                            </div>
                          ))}
                        </div>
                        {blocked && <div className="mt-[8px] h-px w-full shrink-0 bg-[#e5e7eb]" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bottom section - pinned to bottom */}
              <div className="flex flex-col gap-[24px]">
                <div className="flex flex-col gap-[6px]">
                  {/* Subtotal */}
                  <div className="flex justify-between items-center">
                    <p className="font-poppins font-medium text-[16px] text-[#1a1a2e] leading-[21px]">
                      {translator.translate(language, "shipping", "otcCartSlider/subtotal")}
                    </p>
                    <p className="font-poppins text-[16px] text-[#1a1a2e] font-semibold leading-[21px] text-right">
                      {translator.getPrice(total, cartCurrency?.locale, cartCurrency?.rate)}
                    </p>
                  </div>

                  {/* Total savings */}
                  {totalDiscount > 0 && (
                    <div className="flex justify-between items-center">
                      <p className="font-poppins text-[14px] text-[#61a333] leading-[20px]">
                        {translator.translate(language, "shipping", "otcCartSlider/total_savings")}
                      </p>
                      <p className="font-poppins text-[14px] text-[#61a333] font-semibold leading-[20px] text-right">
                        {translator.getPrice(totalDiscount, cartCurrency?.locale, cartCurrency?.rate)}
                      </p>
                    </div>
                  )}

                  {/* Shipping fee */}
                  {isOtcOnlyFlow && freeShippingThreshold !== null && (
                    <div className="flex justify-between items-center">
                      <p className="font-poppins text-[14px] text-[#656d79] leading-[20px]">
                        {translator.translate(language, "shipping", "otcCartSlider/shipping_fee")}
                      </p>
                      <p className="font-poppins text-[14px] text-[#656d79] font-semibold leading-[20px] text-right">
                        {translator.getPrice(total >= freeShippingThreshold ? 0 : shippingFeeOTC, cartCurrency?.locale, cartCurrency?.rate)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Shipping progress bar */}
                {isOtcOnlyFlow && freeShippingThreshold !== null && (
                  <ShippingProgressBarV25
                    currentAmount={total}
                    freeShippingThreshold={freeShippingThreshold}
                    currency="€"
                    language={language}
                  />
                )}

                {/* Checkout button */}
                <button
                  id="to-shipping-button"
                  className="w-full bg-primary-button-gradient text-white font-poppins font-medium text-[20px] leading-none px-[32px] py-[20px] rounded-[12px] flex items-center justify-center transition-all duration-300 group relative overflow-hidden border-0"
                  disabled={isSubmitButtonDisabled || hasRiskBlock}
                  style={{
                    opacity: isSubmitButtonDisabled || hasRiskBlock ? 0.32 : 1,
                    cursor: isSubmitButtonDisabled || hasRiskBlock ? "not-allowed" : "pointer",
                    pointerEvents: isSubmitButtonDisabled || hasRiskBlock ? "none" : "auto"
                  }}
                  onClick={handleToCheckoutClick}
                >
                  <span className="absolute inset-0 bg-[#61a333] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-[12px]"></span>
                  <span className="relative z-10 flex items-center justify-center text-white group-hover:!text-white gap-[8px]">
                    <span className="bg-white rounded-[17px] w-[30px] h-[30px] flex items-center justify-center text-[#1a1a2e] font-poppins font-semibold text-[18px] leading-[30px]">
                      {convertedProducts.length}
                    </span>
                    <span className="text-white group-hover:!text-white">
                      {translator.translate(language, "shipping", "otcCartSlider/toShipping")}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CartSliderV25
